/**
 * Customer-facing neighborhood insights card, shown when a border is clicked on
 * the search map. Curated, non-sensitive stats: inventory mix, sales / selling
 * rate, and engagement. Bilingual + RTL aware.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AreaInsights, KeyCount } from '../../../services/neighborhoodMapService';
import { scoreTint } from '../../../services/neighborhoodMapService';

// District-score factor icons — labels come from i18n
// (search.location.scoreFactors.*), one source of truth for the vocabulary.
const SCORE_FACTOR_ICONS: Record<string, string> = {
  livability: 'ri-community-line',
  investment: 'ri-line-chart-line',
  family: 'ri-parent-line',
  safety: 'ri-shield-check-line',
};

const CAT_LABELS: Record<string, { en: string; ar: string; icon: string }> = {
  villa: { en: 'Villas', ar: 'فلل', icon: 'villa' },
  apartment: { en: 'Apartments', ar: 'شقق', icon: 'apartment' },
  building: { en: 'Buildings', ar: 'عمائر', icon: 'building' },
  land: { en: 'Land', ar: 'أراضٍ', icon: 'land' },
  commercial: { en: 'Commercial', ar: 'تجاري', icon: 'shop' },
  other: { en: 'Other', ar: 'أخرى', icon: 'building' },
};

interface Props {
  insights: AreaInsights | null;
  loading: boolean;
  direction: 'rtl' | 'ltr';
  locale: string;
  onClose: () => void;
}

export default function NeighborhoodInsightsPanel({ insights, loading, direction, locale, onClose }: Props) {
  const { t } = useTranslation('search');
  const isRTL = direction === 'rtl';
  const num = (v: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v);
  const catLabel = (k: string) => (isRTL ? CAT_LABELS[k]?.ar : CAT_LABELS[k]?.en) || k;

  return (
    <div
      // Solid white on a hairline border and one neutral shadow step. This was
      // a glass panel — a white gradient plus backdrop-blur-xl plus a leftover
      // ring-violet-500/10 from the dead purple theme. THEME.md §3 rules 4 and
      // 7: no gradients, no glass; surfaces separate by border and shadow.
      // Over a map, the blur also made the text underneath it hard to read.
      className="absolute bottom-4 z-30 max-h-[calc(100%-2rem)] w-[320px] max-w-[90vw] overflow-y-auto overscroll-contain rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-[0_12px_32px_-12px_rgba(0,77,64,0.28)]"
      style={isRTL ? { right: 16 } : { left: 16 }}
      dir={direction}
    >
      {loading || !insights ? (
        <div className="flex items-center justify-center py-10 text-[var(--pv-color-primary)]">
          <i className="ri-loader-4-line animate-spin text-2xl" />
        </div>
      ) : (
        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <i className="ri-map-pin-2-line shrink-0 text-[1.25rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h3 className="truncate font-bold text-gray-900">{isRTL ? insights.nameAr : insights.nameEn}</h3>
                  {insights.score != null && (
                    <span
                      className="inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold text-white"
                      style={{ backgroundColor: scoreTint(insights.score).fill }}
                      title={t('search.location.districtScore', { defaultValue: 'District score' })}
                    >
                      <i className="ri-star-fill text-[10px]" aria-hidden />
                      {insights.score.toFixed(1)}
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-gray-400">{isRTL ? insights.cityNameAr : insights.cityNameEn}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={isRTL ? 'إغلاق' : 'Close'}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--pv-color-text-muted)] transition-colors hover:bg-[var(--pv-color-surface-sunken)] hover:text-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/40"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Kpi icon="ri-building-line" label={isRTL ? 'إعلانات نشطة' : 'Active listings'} value={num(insights.activeListings)} />
            <Kpi icon="ri-line-chart-line" label={isRTL ? 'نسبة البيع' : 'Selling rate'} value={`${insights.sellingRate}%`} />
            <Kpi icon="ri-eye-line" label={isRTL ? 'مشاهدات' : 'Views'} value={num(insights.propertyViews + insights.areaViews)} />
            <Kpi icon="ri-handbag-line" label={isRTL ? 'تم بيعها' : 'Sold / rented'} value={num(insights.sold + insights.rented)} />
          </div>

          {insights.avgPrice > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] px-3 py-2">
              <span className="text-xs text-gray-500">{isRTL ? 'متوسط السعر' : 'Avg. price'}</span>
              <span className="text-sm font-bold text-[#003930]">{num(insights.avgPrice)} {isRTL ? 'ر.س' : 'SAR'}</span>
            </div>
          )}

          {/* District score breakdown — only factors with real data render */}
          {insights.score != null && insights.scoreComponents && (
            <div className="mb-3">
              <div className="text-xs font-medium text-gray-500 mb-1.5">{t('search.location.districtScore', { defaultValue: 'District score' })}</div>
              <div className="space-y-1.5">
                {Object.entries(insights.scoreComponents)
                  .filter(([, v]) => v != null)
                  .map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="flex w-24 items-center gap-1 truncate text-xs text-gray-600">
                        <i className={`${SCORE_FACTOR_ICONS[key] || 'ri-star-line'} text-[var(--pv-color-primary)]`} aria-hidden />
                        {t(`search.location.scoreFactors.${key}`, { defaultValue: key })}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${((value as number) / 5) * 100}%`, backgroundColor: scoreTint(value as number).fill }}
                        />
                      </div>
                      <span className="w-8 text-right text-xs font-semibold text-gray-700">{(value as number).toFixed(1)}</span>
                    </div>
                  ))}
              </div>
              {insights.scoreSampleSize != null && (
                <div className="mt-1 text-[10px] text-gray-400">
                  {t('search.location.districtScoreComputedFrom', { count: insights.scoreSampleSize })}
                </div>
              )}
            </div>
          )}

          {/* Inventory mix */}
          {insights.byCategory.length > 0 && (
            <Block title={isRTL ? 'المعروض حسب النوع' : 'Inventory by type'} items={insights.byCategory} catLabel={catLabel} total={insights.activeListings} num={num} color="#004D40" />
          )}

          {/* Sold by type */}
          {insights.soldByCategory.length > 0 && (
            <Block title={isRTL ? 'الأكثر مبيعاً' : 'Most sold'} items={insights.soldByCategory} catLabel={catLabel} total={insights.sold + insights.rented} num={num} color="#22c55e" />
          )}

          <Link
            to={`/search?areaId=${insights.areaId}`}
            className="mt-1 block rounded-xl bg-[#004D40] py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-[#003930] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/40 focus-visible:ring-offset-2"
          >
            {isRTL ? 'تصفّح عقارات الحي' : 'Browse listings here'}
          </Link>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--pv-border-soft)] bg-white/70 px-2.5 py-2">
      <div className="flex items-center gap-1 text-[11px] text-gray-500"><i className={`${icon} text-[var(--pv-color-primary)]`} /> {label}</div>
      <div className="text-base font-bold text-gray-900">{value}</div>
    </div>
  );
}

function Block({ title, items, catLabel, total, num, color }: {
  title: string; items: KeyCount[]; catLabel: (k: string) => string; total: number; num: (v: number) => string; color: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="mb-3">
      <div className="text-xs font-medium text-gray-500 mb-1.5">{title}</div>
      <div className="space-y-1.5">
        {items.sort((a, b) => b.count - a.count).slice(0, 5).map((i) => (
          <div key={i.key} className="flex items-center gap-2">
            <span className="text-xs text-gray-600 w-20 truncate">{catLabel(i.key)}</span>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(i.count / max) * 100}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-8 text-right">{num(i.count)}</span>
          </div>
        ))}
      </div>
      {total > 0 && <div className="text-[10px] text-gray-300 mt-1">{num(total)} total</div>}
    </div>
  );
}
